import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { toJpeg } from "html-to-image";
import { fileUrl, getPosterData } from "../lib/api";
import { useLang } from "../lib/i18n";
import LangToggle from "../components/LangToggle";

const BG = "/thank-you-bg.png";

/* Card is designed at these EXACT pixel dimensions and scales as a whole
   using CSS transform, so mobile, desktop and downloaded JPG all look
   pixel-identical. */
const CARD_W = 820;
const CARD_H = Math.round((CARD_W * 1370) / 768); // = 1462

/* Font families — royal, high-contrast */
const F_SCRIPT   = "'Great Vibes','Dancing Script',cursive";
const F_TITLE    = "'Cinzel','Playfair Display',serif";
const F_HEADING  = "'Playfair Display','Cormorant Garamond',serif";
const F_BODY_TE  = "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif";
const F_BODY_EN  = "'Cormorant Garamond','Playfair Display',serif";

/* Colour palette — darker for legibility on cream background */
const C_INK      = "#3B0F26";  // deepest plum — body copy
const C_PLUM     = "#5C1638";  // secondary text
const C_ROSE     = "#8E1F52";  // headings
const C_GOLD_D   = "#7E5A16";  // deep gold — accents
const C_GOLD_L   = "#B8862A";  // light gold — highlights

const Locket = ({ src, size = 130 }) => (
  <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        border: "5px solid transparent",
        background:
          "linear-gradient(#FFF,#FFF) padding-box, conic-gradient(from 0deg, #FFE9A8, #D4AF37, #FFE9A8, #8A5B14, #D4AF37) border-box",
        boxShadow:
          "0 12px 30px -8px rgba(90,20,55,0.55), 0 0 0 2px rgba(255,255,255,0.9), 0 0 0 4px rgba(212,175,55,0.35)",
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} crossOrigin="anonymous" />
      ) : (
        <div style={{ width: "100%", height: "100%", background: "#F5C8DC" }} />
      )}
    </div>
  </div>
);

const HeartDivider = () => (
  <svg viewBox="0 0 180 20" width="180" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block" }}>
    <line x1="0"   y1="10" x2="82"  y2="10" stroke={C_GOLD_D} strokeWidth="1.2" />
    <path d="M 90 5 Q 95 0, 98 6 Q 100 0, 102 6 Q 104 12, 90 15 Q 76 12, 78 6 Q 80 0, 82 6 Q 85 0, 90 5 Z" fill={C_GOLD_D} />
    <line x1="98"  y1="10" x2="180" y2="10" stroke={C_GOLD_D} strokeWidth="1.2" />
  </svg>
);

/** Small decorative balloon — scattered across empty card space to fill gaps. */
const Balloon = ({ size = 24, color = "#E36FA6", stroke = "#8E1F52", tail = "left", style = {} }) => (
  <svg
    width={size}
    height={size * (60 / 42)}
    viewBox="0 0 42 60"
    xmlns="http://www.w3.org/2000/svg"
    style={{ position: "absolute", pointerEvents: "none", ...style }}
    aria-hidden="true"
  >
    <defs>
      <radialGradient id={`b-${color.replace("#", "")}`} cx="0.35" cy="0.35" r="0.75">
        <stop offset="0" stopColor="#FFFFFF" stopOpacity="0.85" />
        <stop offset="1" stopColor={color} />
      </radialGradient>
    </defs>
    <ellipse cx="21" cy="22" rx="16" ry="20" fill={`url(#b-${color.replace("#", "")})`} stroke={stroke} strokeWidth="0.6" />
    <polygon points="19,42 23,42 21,46" fill={stroke} />
    <path
      d={tail === "left" ? "M 21 46 Q 24 52, 20 58" : "M 21 46 Q 18 52, 22 58"}
      fill="none" stroke={C_GOLD_D} strokeWidth="0.7"
    />
  </svg>
);

/**
 * Small eyebrow chip for section titles — high contrast on cream bg.
 * Both "Ram & Prajwala's Message" and "Princess Anagha's Message" chips
 * share IDENTICAL dimensions so they visually match.
 */
const SectionHeader = ({ children }) => (
  <p
    style={{
      display: "inline-block",
      minWidth: "320px",
      padding: "2px 12px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.75)",
      border: `1px solid ${C_GOLD_L}55`,
      boxShadow: "0 2px 6px -3px rgba(90,20,55,0.25)",
      fontFamily: F_TITLE,
      color: C_ROSE,
      letterSpacing: "0.2em",
      fontSize: "12.5px",
      fontWeight: 700,
      textTransform: "uppercase",
      margin: 0,
      whiteSpace: "nowrap",
    }}
  >
    ✦ {children} ✦
  </p>
);

export default function Poster() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const langGlobal = useLang();
  const langOverride = params.get("lang");
  const lang = langOverride === "te" || langOverride === "en" ? langOverride : langGlobal;
  const [g, setG] = useState(null);
  const [err, setErr] = useState("");
  const [downloading, setDownloading] = useState(false);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Measure the wrapper width and compute the CSS transform scale so the
  // fixed-pixel card visually fits every viewport identically.
  useLayoutEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setScale(Math.min(1, w / CARD_W));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener("resize", compute);
    return () => { ro.disconnect(); window.removeEventListener("resize", compute); };
  }, [g]);

  useEffect(() => {
    getPosterData(id).then(setG).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [id]);

  const switchLang = (l) => {
    const next = new URLSearchParams(params);
    next.set("lang", l);
    setParams(next, { replace: true });
    try { window.localStorage.setItem("anagha.lang", l); } catch { /* */ }
  };

  const downloadJpg = async () => {
    if (!canvasRef.current) return;
    setDownloading(true);
    const node = canvasRef.current;
    const originalTransform = node.style.transform;
    // Temporarily remove the scale transform so html-to-image captures the
    // card at its native 820×1462 dimensions → identical output regardless
    // of the browser viewport.
    node.style.transform = "none";
    try {
      const dataUrl = await toJpeg(node, {
        quality: 0.98,
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: "#FFF3F8",
        width: CARD_W,
        height: CARD_H,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `anagha-thank-you-${(g?.name || "royal").replace(/\s+/g, "-").toLowerCase()}.jpg`;
      a.click();
    } catch (e) {
      alert("Could not generate JPG. Please try again or take a screenshot.");
    } finally {
      node.style.transform = originalTransform;
      setDownloading(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen grid place-items-center bg-rose-50 text-plum p-8 text-center"
           style={{ fontFamily: F_BODY_TE }}>
        <p>{err}</p>
      </div>
    );
  }
  if (!g) {
    return (
      <div className="min-h-screen grid place-items-center bg-rose-50 text-plum p-8"
           style={{ fontFamily: F_BODY_TE }}>
        <p>Loading royal thank-you card…</p>
      </div>
    );
  }

  const photoSrc = fileUrl(g.character_path || g.selfie_path);
  const guestName = g.name || "";

  // Detect if the stored personal_message is Telugu — if so, only show it
  // when lang=te; on English cards fall back to a generic English blessing.
  const isTeluguText = (s) => !!s && /[\u0C00-\u0C7F]/.test(s);
  const storedMsg = g.personal_message || "";
  const storedIsTe = isTeluguText(storedMsg);
  const anaghaTE = (storedIsTe && storedMsg) ||
    "మీ ఆశీర్వాదాలు నా జీవితంలో నక్షత్రాల్లా మెరుస్తాయి! చాలా బాగా వచ్చినందుకు ధన్యవాదాలు.";
  const anaghaEN = (!storedIsTe && storedMsg) ||
    "Your blessings shine like stars in my life! Thank you so much for coming to my party.";

  const parentsTE =
    "మీ రాక ఈ రోజును మధురమైన జ్ఞాపకంగా మార్చింది. మీ ప్రేమకు, ఆశీర్వాదాలకు, ఆప్యాయతకు హృదయపూర్వక కృతజ్ఞతలు.";
  const parentsEN =
    "Your presence turned this day into a memory we will treasure forever. Thank you for your love, blessings and warmth from the bottom of our hearts.";

  return (
    <div
      className="min-h-screen w-full py-6 sm:py-10 px-4 flex flex-col items-center"
      data-testid="poster-page"
      style={{ background: "radial-gradient(circle at 50% 0%, #FFE7EF 0%, #FDF9F4 60%, #FFF3F8 100%)" }}
    >
      {/* Language switcher — updates the URL ?lang= param so the card
          can be shared in a specific language via a link. */}
      <div className="absolute top-3 right-3 z-50 flex items-center gap-2" data-testid="poster-lang-switch">
        <button
          onClick={() => switchLang("te")}
          className="text-xs px-3 py-1 rounded-full transition-all"
          style={{
            background: lang === "te" ? C_ROSE : "rgba(255,255,255,0.85)",
            color: lang === "te" ? "#FFF" : C_INK,
            border: `1px solid ${C_ROSE}55`,
          }}
          data-testid="poster-lang-te"
        >తెలుగు</button>
        <button
          onClick={() => switchLang("en")}
          className="text-xs px-3 py-1 rounded-full transition-all"
          style={{
            background: lang === "en" ? C_ROSE : "rgba(255,255,255,0.85)",
            color: lang === "en" ? "#FFF" : C_INK,
            border: `1px solid ${C_ROSE}55`,
          }}
          data-testid="poster-lang-en"
        >English</button>
        <div className="hidden"><LangToggle /></div>
      </div>

      {/* Download banner */}
      <div
        className="mb-5 w-full flex flex-col sm:flex-row items-center justify-between gap-3 max-w-[820px] rounded-2xl px-4 py-3"
        style={{
          background: "rgba(255,255,255,0.9)",
          border: `1px solid ${C_ROSE}44`,
          backdropFilter: "blur(10px)",
        }}
        data-testid="poster-download-banner"
      >
        <p style={{ fontFamily: F_BODY_TE, color: C_PLUM, fontSize: 13, margin: 0 }}>
          {lang === "te"
            ? "✦ మీ ప్రత్యేక 'ధన్యవాదాలు' కార్డ్ — HD క్వాలిటీలో డౌన్‌లోడ్ చేసుకోండి."
            : "✦ Your personal 'Thank You' keepsake — download in HD quality."}
        </p>
        <button
          onClick={downloadJpg}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-semibold hover:brightness-110 transition-all disabled:opacity-60 shrink-0"
          style={{
            background: "linear-gradient(180deg,#E36FA6 0%,#8E1F52 100%)",
            color: "#FFF",
            boxShadow: "0 8px 20px -6px rgba(90,20,55,0.55)",
          }}
          data-testid="poster-download-btn"
        >
          <Download size={15} />
          {downloading
            ? (lang === "te" ? "తయారవుతోంది..." : "Preparing HD...")
            : (lang === "te" ? "HD JPG డౌన్‌లోడ్" : "Download HD JPG")}
        </button>
      </div>

      {/* Scaling wrapper — reserves the visual (scaled) space and centers the card */}
      <div
        ref={wrapperRef}
        data-testid="poster-scaler"
        style={{
          width: "min(100%, 820px)",
          height: CARD_H * scale,
          position: "relative",
        }}
      >
        {/* THE CARD — fixed 820x1462; scaled via CSS so mobile/desktop/download all match. */}
        <div
          ref={canvasRef}
          id="poster-canvas"
          data-testid="poster-canvas"
          className="relative overflow-hidden"
          style={{
            width: CARD_W,
            height: CARD_H,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            backgroundImage: `url("${BG}")`,
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            borderRadius: "18px",
            boxShadow: "0 30px 80px -20px rgba(90,20,55,0.35)",
          }}
        >
        {/* Decorative balloons scattered across empty card space —
            positioned OUTSIDE the content region and away from the
            cartouche/castle/carriage art. Purely cosmetic. */}
        <div data-testid="poster-balloon-layer" style={{ position: "absolute", inset: 0, zIndex: 3, pointerEvents: "none" }}>
          {/* Upper-left area, between drapery and cartouche */}
          <Balloon size={22} color="#E36FA6" stroke="#8E1F52" tail="left"  style={{ left: "16%", top: "8%" }} />
          <Balloon size={18} color="#F5B5CE" stroke="#B85787" tail="right" style={{ left: "22%", top: "13%" }} />
          {/* Upper-right area */}
          <Balloon size={22} color="#F5C46E" stroke={C_GOLD_D} tail="right" style={{ right: "16%", top: "8%" }} />
          <Balloon size={18} color="#FFD7E5" stroke="#B85787" tail="left"  style={{ right: "22%", top: "13%" }} />
          {/* Left side beside FOR COMING TO block */}
          <Balloon size={22} color="#F5B5CE" stroke="#B85787" tail="right" style={{ left: "10%", top: "43%" }} />
          {/* Right side beside FOR COMING TO block */}
          <Balloon size={22} color="#F5C46E" stroke={C_GOLD_D} tail="left"  style={{ right: "10%", top: "43%" }} />
          {/* Left side beside Ram & Prajwala message header */}
          <Balloon size={18} color="#E36FA6" stroke="#8E1F52" tail="left"  style={{ left: "12%", top: "60%" }} />
          {/* Right side beside Anagha message */}
          <Balloon size={18} color="#F5C46E" stroke={C_GOLD_D} tail="right" style={{ right: "12%", top: "60%" }} />
        </div>
        {/* Inside the cartouche opening — small guest PNG + Thank You caption. */}
        <div
          style={{
            position: "absolute",
            top: "23%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "auto",
            textAlign: "center",
            zIndex: 4,
          }}
          data-testid="poster-cartouche-content"
        >
          <p
            data-testid="poster-thank-you"
            style={{
              fontFamily: F_SCRIPT,
              fontSize: "48px",
              lineHeight: 1,
              padding: "4px 0",
              color: C_ROSE,
              textShadow: "0 2px 0 rgba(255,255,255,0.85), 0 0 10px rgba(255,255,255,0.6)",
              margin: 0,
              fontWeight: 700,
            }}
          >
            Thank You
          </p>
        </div>

        {/* Guest info — sits between the cartouche and the picture. */}
        <div
          data-testid="poster-guest-info"
          style={{
            position: "absolute",
            top: "37%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "88%",
            textAlign: "center",
            zIndex: 4,
          }}
        >
          <p
            style={{
              fontFamily: F_TITLE,
              fontSize: "11.5px",
              color: C_GOLD_D,
              letterSpacing: "0.28em",
              fontWeight: 600,
              margin: 0,
              textTransform: "uppercase",
            }}
          >
            {lang === "te" ? "మా ప్రియమైన అతిథి" : "To our dear guest"}
          </p>
          <p
            style={{
              fontFamily: F_HEADING,
              fontSize: "30px",
              color: C_ROSE,
              lineHeight: 1.05,
              textShadow: "0 2px 0 rgba(255,255,255,0.55)",
              marginTop: 2,
              fontWeight: 700,
            }}
            data-testid="poster-guest-name"
          >
            {guestName}
          </p>
          {(g.loving_title || g.role_title) && (
            <p
              style={{
                fontFamily: lang === "en" ? F_BODY_EN : F_BODY_TE,
                fontSize: "14px",
                color: C_PLUM,
                fontStyle: "italic",
                marginTop: 2,
                fontWeight: 500,
              }}
              data-testid="poster-loving-title"
            >
              — {g.loving_title || g.role_title} —
            </p>
          )}
        </div>

        {/* Guest photo — sits UNDER the guest info block, circular gold-ring locket */}
        <div
          style={{
            position: "absolute",
            top: "42.5%",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 4,
          }}
          data-testid="poster-guest-locket"
        >
          <Locket src={photoSrc} size={150} />
        </div>

        {/* Middle content stack — starts BELOW the guest picture and ends
            ABOVE the castle/carriage art. justifyContent:space-between
            fills the whole card evenly with no gaps at the bottom. */}
        <div
          style={{
            position: "absolute",
            top: "54%",
            bottom: "19%",
            left: "8%",
            right: "8%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            textAlign: "center",
            zIndex: 4,
          }}
          data-testid="poster-content"
        >
          {/* Block A — FOR COMING TO Birthday */}
          <div style={{ width: "100%" }}>
            <p
              style={{
                fontFamily: F_TITLE,
                color: C_GOLD_D,
                letterSpacing: "0.32em",
                fontSize: "14px",
                fontWeight: 700,
                margin: 0,
                textShadow: "0 1px 0 rgba(255,255,255,0.6)",
              }}
            >
              FOR COMING TO
            </p>
            <p
              style={{
                fontFamily: F_SCRIPT,
                fontSize: "26px",
                lineHeight: 1.05,
                color: C_ROSE,
                textShadow: "0 2px 0 rgba(255,255,255,0.55)",
                marginTop: 2,
              }}
            >
              Our Little Princess&rsquo;s
            </p>
            <p
              style={{
                fontFamily: F_SCRIPT,
                fontSize: "40px",
                lineHeight: 0.95,
                color: C_ROSE,
                textShadow: "0 3px 0 rgba(255,255,255,0.55)",
                marginTop: 2,
                fontWeight: 700,
              }}
            >
              Birthday!
            </p>
            <div style={{ marginTop: 4 }}>
              <HeartDivider />
            </div>
          </div>

          {/* Block B — Ram & Prajwala's Message */}
          <div style={{ width: "92%" }} data-testid="poster-msg-parents">
            <SectionHeader>
              {lang === "te" ? "రామ్ & ప్రజ్వల నుండి" : "Ram & Prajwala's Message"}
            </SectionHeader>
            <p
              style={{
                fontFamily: lang === "te" ? F_BODY_TE : F_BODY_EN,
                color: C_INK,
                fontSize: "18px",
                lineHeight: 1.5,
                marginTop: 6,
                fontWeight: 500,
              }}
              data-testid="poster-msg-parents-body"
            >
              {lang === "te" ? parentsTE : parentsEN}
            </p>
          </div>

          {/* Block D — Princess Anagha's Message */}
          <div style={{ width: "92%" }} data-testid="poster-msg-anagha">
            <SectionHeader>
              {lang === "te" ? "రాజకుమారి అనఘ సందేశం" : "Princess Anagha's Message"}
            </SectionHeader>
            <p
              style={{
                fontFamily: lang === "te" ? F_BODY_TE : F_BODY_EN,
                color: C_INK,
                fontSize: "18px",
                lineHeight: 1.5,
                fontStyle: "italic",
                marginTop: 6,
                fontWeight: 500,
              }}
              data-testid="poster-msg-anagha-body"
            >
              &ldquo;{lang === "te" ? anaghaTE : anaghaEN}&rdquo;
            </p>
            <p
              style={{
                fontFamily: F_SCRIPT,
                fontSize: "22px",
                color: C_ROSE,
                lineHeight: 1.05,
                marginTop: 14,
                textShadow: "0 2px 0 rgba(255,255,255,0.55)",
              }}
            >
              — Lakshmi Anagha Dhruthi
            </p>
            <p
              style={{
                fontFamily: F_TITLE,
                color: C_GOLD_D,
                fontSize: "11px",
                letterSpacing: "0.24em",
                marginTop: 4,
                fontWeight: 600,
              }}
            >
              ✦ 25 AUGUST 2026 ✦
            </p>
          </div>
        </div>
        </div>
      </div>

      <Link
        to="/admin"
        className="fixed top-3 right-3 text-xs text-plum hover:text-black hidden bg-white/70 rounded-full px-3 py-1.5"
        data-testid="poster-admin-link"
      >
        Admin
      </Link>
    </div>
  );
}
